import { CommonModule, Location } from '@angular/common';
import { Component, OnDestroy, OnInit } from '@angular/core';
import { ReactiveFormsModule, UntypedFormBuilder, UntypedFormControl, UntypedFormGroup } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatExpansionModule } from '@angular/material/expansion';
import { MatIconModule } from '@angular/material/icon';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatRadioModule, MatRadioChange } from '@angular/material/radio';
import { MatDialog, MatDialogRef } from '@angular/material/dialog';
import { ConfirmationDialogComponent } from 'src/app/shared/dialog/confitmation-dialog/confirmation-dialog.component';
import { ActivatedRoute, Router } from '@angular/router';
import { of } from 'rxjs';
import { catchError, first } from 'rxjs/operators';
import { OtherTruckType, TruckType } from 'src/app/registration/shared/models/truck-type.model';
import { RegistrationService } from 'src/app/registration/shared/registration.service';
import { DateTimeService } from 'src/app/shared/date-time-convertor/date-time.service';
import { DrivingExperience } from 'src/app/shared/models/driving-experience.model';
import { ROUTE_DQF, ROUTE_DQF_UPLOADS } from 'src/app/shared/routes';
import { DriverApiService } from 'src/app/shared/services/driver-api.service';
import { environment } from 'src/environments/environment.dev';
import { SubSink } from 'subsink';
import { MilesDriven } from '../shared/models/miles-driven.model';
import { TruckTypeComponent } from '../truck-type/truck-type.component';

const STRAIGHT_TRUCK = 'straightTruck';
const TRACTOR_AND_SEMI_TRAILER = 'tractorAndSemiTrailer';
const TRACTOR_TWO_TRAILERS = 'tractorTwoTrailers';
const OTHERS = 'others';

const ACTION_SAVE_DRIVING_EXPERIENCE = 'Save driving experience';
const ACTION_UPDATE_DRIVING_EXPERIENCE = 'Update driving experience';

@Component({
  selector: 'app-driving-experience',
  templateUrl: './driving-experience.component.html',
  styleUrls: ['./driving-experience.component.scss'],
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    MatButtonModule,
    MatCheckboxModule,
    MatExpansionModule,
    MatIconModule,
    MatProgressSpinnerModule,
    MatRadioModule,
    TruckTypeComponent
  ]
})
export class DrivingExperienceComponent implements OnInit, OnDestroy {
  form: UntypedFormGroup;
  loading: boolean;
  errorMessage: string;
  validationErrorMessage: string;
  milesDrivenOptions: MilesDriven[];
  dqfId: string;
  panelOpenState = false;
  totalYears = 0;
  totalMonths = 0;
  truckHasValue = false;
  noTruckingExperience = false;
  truckTypes = ['straightTruck', 'tractorAndSemiTrailer', 'tractorTwoTrailers', 'others'];
  showCancelButton = false;
  disableAddNew = false;
  private subs = new SubSink();

  constructor(
    private readonly fb: UntypedFormBuilder,
    private readonly location: Location,
    private readonly registrationService: RegistrationService,
    private readonly timeService: DateTimeService,
    private readonly driverApiService: DriverApiService,
    private readonly router: Router,
    private readonly activatedRoute: ActivatedRoute,
    private readonly dialog: MatDialog
  ) {
    this.form = this.createForm();
    this.loading = false;
    this.errorMessage = undefined;
  }

  ngOnInit() {
    this.loadDrivingExperience();

    this.registrationService
      .getMilesDriven()
      .pipe(first())
      .subscribe((milesDrivenOptions: MilesDriven[]) => {
        this.milesDrivenOptions = milesDrivenOptions;
      });

    // Subscribe to form changes to update validation error message
    this.subs.add(
      this.form.valueChanges.subscribe(() => {
        this.updateValidationErrorMessage();
      })
    );
  }

  loadDrivingExperience() {
    this.subs.add(
      this.registrationService.loadDriver().subscribe(res => {
        if (res.experience) {
          this.populateForm(res.experience);
        }
      })
    );
  }

  onYearChange() {
    this.totalYears = 0;
    for (let i = 0; i < this.truckTypes.length; i++) {
      this.totalYears += this.form.controls[this.truckTypes[i]].value.years || 0;
    }
    this.updateValidationErrorMessage();
  }

  onMonthChange() {
    this.totalMonths = 0;
    for (let i = 0; i < this.truckTypes.length; i++) {
      this.totalMonths += this.form.controls[this.truckTypes[i]].value.months || 0;
    }

    if (this.totalMonths >= 12) {
      let years = Math.floor(this.totalMonths / 12);
      let months = this.totalMonths % 12;
      this.totalYears += years;
      this.totalMonths = months;
    }
    this.updateValidationErrorMessage();
  }

  updateValidationErrorMessage(): void {
    if (this.noTruckingExperience || !this.showCancelButton) {
      this.validationErrorMessage = '';
      return;
    }

    const missingFields: string[] = [];
    const truckTypeLabels = {
      straightTruck: 'Straight Truck',
      tractorAndSemiTrailer: 'Tractor and Semi-Trailer',
      tractorTwoTrailers: 'Tractor-Two Trailers',
      others: 'Others'
    };

    // Check each truck type
    for (const truckType of this.truckTypes) {
      const control = this.form.controls[truckType];
      
      if (truckType === 'others') {
        // For Others section, check if equipment is filled
        const equipment = control.get('equipment')?.value;
        if (equipment) {
          const years = control.get('years')?.value;
          const months = control.get('months')?.value;
          const milesDriven = control.get('milesDriven')?.value;
          
          const missing: string[] = [];
          if (!years && years !== 0) missing.push('Years');
          if (!months && months !== 0) missing.push('Months');
          if (!milesDriven) missing.push('Miles Driven');
          
          if (missing.length > 0) {
            missingFields.push(`${truckTypeLabels[truckType]}: ${missing.join(', ')}`);
          }
        }
      } else {
        // For other truck types, check if any checkbox is selected
        const hasSelection = 
          control.get('van')?.value ||
          control.get('flat')?.value ||
          control.get('tank')?.value ||
          control.get('dumpTruck')?.value ||
          control.get('reefer')?.value ||
          control.get('otherTruckType')?.value;
        
        if (hasSelection) {
          const years = control.get('years')?.value;
          const months = control.get('months')?.value;
          const milesDriven = control.get('milesDriven')?.value;
          
          const missing: string[] = [];
          if (!years && years !== 0) missing.push('Years');
          if (!months && months !== 0) missing.push('Months');
          if (!milesDriven) missing.push('Miles Driven');
          
          if (missing.length > 0) {
            missingFields.push(`${truckTypeLabels[truckType]}: ${missing.join(', ')}`);
          }
        }
      }
    }

    if (missingFields.length > 0) {
      this.validationErrorMessage = `Please complete the following required fields: ${missingFields.join(' | ')}`;
    } else {
      this.validationErrorMessage = '';
    }
  }

  ngOnDestroy() {
    this.subs.unsubscribe();
  }

  onSubmit(): void {
    if (!this.form.valid) {
      return;
    }

    this.loading = true;
    const model = this.prepareSaveModel();

    if (!model.id) {
      this.subs.add(
        this.registrationService
          .saveDrivingExperience(model)
          .pipe(
            catchError((error: any) => {
              this.errorMessage = environment.errorMessage;
              this.loading = false;
              return of(false);
            })
          )
          .subscribe(() => {
            this.errorMessage = undefined;
            this.loading = false;
            this.goBack();
          })
      );
    } else {
      this.subs.add(
        this.registrationService
          .updateDrivingExperience(model)
          .pipe(
            catchError((error: any) => {
              this.errorMessage = environment.errorMessage;
              this.loading = false;
              return of(false);
            })
          )
          .subscribe(() => {
            this.errorMessage = undefined;
            this.loading = false;
            this.goBack();
          })
      );
    }
  }

  onNavigateToProfile(): void {
    this.registrationService.navigateToProfile();
  }

  goBack(): void {
    const isFromDQF = this.isNavigatedFromDQF();
    if (isFromDQF) {
      // Navigate back to DQF uploads page
      this.router.navigateByUrl(`${ROUTE_DQF}/${ROUTE_DQF_UPLOADS}?type=Driver Uploads`);
    } else {
      this.location.back();
    }
  }

  private isNavigatedFromDQF(): boolean {
    // Check if we came from DQF by looking at the referrer or route history
    const referrer = document.referrer;

    // Check if referrer contains DQF routes
    if (referrer && (referrer.includes('/driverQualification/') || referrer.includes('/dqf/'))) {
      return true;
    }

    // Check if there's a DQF context in session storage or navigation state
    const navigationState = this.router.getCurrentNavigation()?.extras?.state;
    if (navigationState && navigationState['fromDQF']) {
      return true;
    }

    // Check if we have DQF-related query params or state
    if (this.activatedRoute.snapshot.queryParams['fromDQF'] ||
      this.activatedRoute.snapshot.queryParams['dqfId']) {
      return true;
    }

    return false;
  }

  getExperienceRadioValue(): string | null {
    if (this.disableAddNew && !this.noTruckingExperience) return 'yes';
    if (this.noTruckingExperience) return 'no';
    return null;
  }

  onExperienceRadioChange(event: MatRadioChange): void {
    if (event.value === 'yes') {
      this.onHasExperience();
    } else {
      const hasExistingData = this.showCancelButton || (this.totalYears > 0 || this.totalMonths > 0);
      if (hasExistingData) {
        this.confirmSwitchToNo(event);
      } else {
        this.onNoExperience();
      }
    }
  }

  private confirmSwitchToNo(event: MatRadioChange): void {
    const dialogRef = this.dialog.open(ConfirmationDialogComponent, {
      width: '400px',
      data: {
        title: 'Remove driving experience?',
        message: 'Switching to <strong>No</strong> will clear all the driving experience details you have entered. This cannot be undone.',
        actions: [
          {
            name: 'Cancel',
            action: () => {
              dialogRef.close();
              event.source.radioGroup.value = 'yes';
            }
          },
          {
            name: 'Proceed',
            action: () => {
              dialogRef.close();
              this.onNoExperience();
            }
          }
        ]
      },
      disableClose: true,
      panelClass: 'modern-confirmation-dialog'
    });
  }

  onHasExperience(): void {
    this.noTruckingExperience = false;
    this.form.get('noTruckingExperience').setValue(false);
    this.disableEnableForm();
    this.addDrivingExperienceRecord();
    this.updateValidationErrorMessage();
  }

  onNoExperience(): void {
    this.disableAddNew = true;
    this.noTruckingExperience = true;
    this.form.get('noTruckingExperience').setValue(true);
    this.disableEnableForm();
    this.autoSaveNoExperience();
    this.updateValidationErrorMessage();
  }

  noDrivingChecked(event): void {
    if (event.checked) {
      this.onNoExperience();
    } else {
      this.disableAddNew = false;
      this.noTruckingExperience = false;
      this.disableEnableForm();
      this.autoSaveNoExperience();
    }
    this.updateValidationErrorMessage();
  }

  private autoSaveNoExperience(): void {
    const model = this.prepareSaveModel();
    if (model.id) {
      // Update existing record
      this.registrationService
        .updateDrivingExperience(model)
        .pipe(
          catchError((error: any) => {
            this.errorMessage = environment.errorMessage;
            return of(false);
          })
        )
        .subscribe(() => {
          this.errorMessage = undefined;
          // Navigate back to DQF if checkbox is checked
          if (this.noTruckingExperience) {
            this.goBack();
          }
        });
    } else {
      // Create new record
      this.registrationService
        .saveDrivingExperience(model)
        .pipe(
          catchError((error: any) => {
            this.errorMessage = environment.errorMessage;
            return of(false);
          })
        )
        .subscribe(() => {
          this.errorMessage = undefined;
          // Navigate back to DQF if checkbox is checked
          if (this.noTruckingExperience) {
            this.goBack();
          }
        });
    }
  }

  addDrivingExperienceRecord(): void {
    this.showCancelButton = true;
    this.disableAddNew = true;
    this.updateValidationErrorMessage();
  }

  cancelAddingExperience(): void {
    this.disableAddNew = false;
    this.resetAndHide();
    // Ensure form controls are enabled after canceling
    if (!this.noTruckingExperience) {
      this.form.get('straightTruck').enable();
      this.form.get('tractorAndSemiTrailer').enable();
      this.form.get('tractorTwoTrailers').enable();
      this.form.get('others').enable();
    }
    this.updateValidationErrorMessage();
  }

  private resetAndHide(): void {
    this.form.get('straightTruck').reset();
    this.form.get('tractorAndSemiTrailer').reset();
    this.form.get('tractorTwoTrailers').reset();
    this.form.get('others').reset();
    this.form.markAsDirty();
    this.totalYears = 0;
    this.totalMonths = 0;
    // Don't disable the form when canceling - keep it enabled for future use
    if (this.form.value.noTruckingExperience) {
      this.form.setErrors(null);
    }
    this.showCancelButton = false;
  }

  private disableEnableForm(): void {
    if (this.noTruckingExperience) {
      this.resetAndHide();
    } else {
      this.form.get('straightTruck').enable();
      this.form.get('tractorAndSemiTrailer').enable();
      this.form.get('tractorTwoTrailers').enable();
      this.form.get('others').enable();
    }
  }

  private prepareSaveModel(): DrivingExperience {
    let model: any = {};

    if (!this.noTruckingExperience) {
      [STRAIGHT_TRUCK, TRACTOR_AND_SEMI_TRAILER, TRACTOR_TWO_TRAILERS].forEach(type => {
        const control = this.form.controls[type];

        control.patchValue({
          type: type,
          displayType:
            type === STRAIGHT_TRUCK
              ? 'Straight Truck'
              : type === TRACTOR_AND_SEMI_TRAILER
                ? 'Tractor and Semi-Trailer'
                : 'Tractor-Two Trailers',
          monthsOfExperience:
            (Number(control.get('years').value) || 0) * 12 +
            (Number(control.get('months').value) || 0),
        });

        const isOtherSelected = control.get('otherTruckType')?.value || false;
        const description = control.get('otherTruckTypeDescription')?.value?.trim() || '';

        control.patchValue({
          otherTruckType: isOtherSelected,
          otherTruckTypeDescription: description
        });
      });

      const othersControl = this.form.controls[OTHERS];
      othersControl.patchValue({
        type: OTHERS,
        displayType: 'Others',
        monthsOfExperience:
          (Number(othersControl.get('years').value) || 0) * 12 +
          (Number(othersControl.get('months').value) || 0),
      });

      model = {
        noTruckingExperience: this.form.value.noTruckingExperience || false,
        truckTypes: this.getTruckTypes(this.form.value),
        id: this.form.value.id || undefined
      };
    } else {
      model = {
        noTruckingExperience: this.form.value.noTruckingExperience || false,
        id: this.form.value.id || undefined
      };
    }

    return model as DrivingExperience;
  }

  private populateForm(model: DrivingExperience): void {
    if (!model.truckTypes.length && !model.noTruckingExperience) {
      this.disableAddNew = false;
      this.noTruckingExperience = false;
    } else if (model.truckTypes.length) {
      this.disableAddNew = true;
      this.noTruckingExperience = false;
      this.showCancelButton = true;
    } else {
      this.disableAddNew = !!model.noTruckingExperience;
      this.showCancelButton = false;
      this.noTruckingExperience = true;
      this.form.setErrors({ inValid: false });
      this.form.updateValueAndValidity();
      this.disableForm();
    }

    this.form.patchValue({
      noTruckingExperience: model.noTruckingExperience,
      id: model.id,
      straightTruck: this.setTruckType(model.truckTypes.find(t => t.type === STRAIGHT_TRUCK)),
      tractorAndSemiTrailer: this.setTruckType(model.truckTypes.find(t => t.type === TRACTOR_AND_SEMI_TRAILER)),
      tractorTwoTrailers: this.setTruckType(model.truckTypes.find(t => t.type === TRACTOR_TWO_TRAILERS)),
      others: this.setTruckType(model.truckTypes.find(t => t.type === OTHERS), true),
    });

    this.onYearChange();
    this.onMonthChange();
  }

  private setTruckType(truckType: any, isOtherTruck = false): any {
    if (!truckType) {
      return {
        years: 0,
        months: 0,
        otherTruckType: false,
        otherTruckTypeDescription: ''
      };
    }

    const monthsOfExperience = Number(truckType.monthsOfExperience) || 0;
    const year = Math.floor(monthsOfExperience / 12);
    const month = monthsOfExperience % 12;

    const patch: any = {
      years: year,
      months: month,
      milesDriven: truckType.milesDriven || '',
      type: truckType.type || '',
      displayType: truckType.displayType || '',
    };

    if (!isOtherTruck) {
      patch.flat = truckType.flat || false;
      patch.tank = truckType.tank || false;
      patch.van = truckType.van || false;
      patch.dumpTruck = truckType.dumpTruck || false;
      patch.reefer = truckType.reefer || false;
      patch.otherTruckType = truckType.otherTruckType || false;
      patch.otherTruckTypeDescription = truckType.otherTruckTypeDescription || '';
    } else {
      patch.equipment = truckType.equipment || '';
    }

    return patch;
  }

  private createForm(): UntypedFormGroup {
    const form = this.fb.group(
      {
        noTruckingExperience: [false],
        id: [],
        straightTruck: this.createTruckFormGroup(),
        tractorAndSemiTrailer: this.createTruckFormGroup(),
        tractorTwoTrailers: this.createTruckFormGroup(),
        others: this.createTruckFormGroup()
      },
      {
        validators: this.checkFormValidity.bind(this)
      }
    );

    return form;
  }

  private createTruckFormGroup(): UntypedFormGroup {
    return this.fb.group({
      flat: new UntypedFormControl(false),
      tank: new UntypedFormControl(false),
      van: new UntypedFormControl(false),
      dumpTruck: new UntypedFormControl(false),
      reefer: new UntypedFormControl(false),
      otherTruckType: new UntypedFormControl(false),
      otherTruckTypeDescription: new UntypedFormControl(null),
      years: new UntypedFormControl(null),
      months: new UntypedFormControl(null),
      equipment: new UntypedFormControl(null),
      milesDriven: new UntypedFormControl(''),
      monthsOfExperience: new UntypedFormControl(),
      type: [''],
      displayType: ['']
    });
  }

  private checkFormValidity(group) {
    if (this.noTruckingExperience) {
      return null;
    }

    const controls = group.controls;
    let hasAnyStartedSection = false;
    let hasAnyCompleteSection = false;
    
    // Check "others" section (equipment-based)
    const equipment = controls['others'].controls['equipment'].value;
    if (equipment) {
      hasAnyStartedSection = true;
      const hasYears = controls['others'].controls['years'].value !== null && controls['others'].controls['years'].value !== '';
      const hasMonths = controls['others'].controls['months'].value !== null && controls['others'].controls['months'].value !== '';
      const hasMilesDriven = controls['others'].controls['milesDriven'].value && controls['others'].controls['milesDriven'].value !== '';
      
      if (hasYears && hasMonths && hasMilesDriven) {
        hasAnyCompleteSection = true;
      } else {
        // Section started but not complete - form is invalid
        return { inValid: true };
      }
    }
    
    // Check other truck types (van, flat, tank, etc.)
    for (let i = 0; i < this.truckTypes.length - 1; i++) {
      const truckTypeSelected = (
        controls[this.truckTypes[i]].controls['van'].value ||
        controls[this.truckTypes[i]].controls['flat'].value ||
        controls[this.truckTypes[i]].controls['tank'].value ||
        controls[this.truckTypes[i]].controls['dumpTruck']?.value ||
        controls[this.truckTypes[i]].controls['reefer'].value ||
        controls[this.truckTypes[i]].controls['otherTruckType']?.value
      );
      
      if (truckTypeSelected) {
        hasAnyStartedSection = true;
        const hasYears = controls[this.truckTypes[i]].controls['years'].value !== null && controls[this.truckTypes[i]].controls['years'].value !== '';
        const hasMonths = controls[this.truckTypes[i]].controls['months'].value !== null && controls[this.truckTypes[i]].controls['months'].value !== '';
        const hasMilesDriven = controls[this.truckTypes[i]].controls['milesDriven'].value && controls[this.truckTypes[i]].controls['milesDriven'].value !== '';
        
        if (hasYears && hasMonths && hasMilesDriven) {
          hasAnyCompleteSection = true;
        } else {
          // Section started but not complete - form is invalid
          return { inValid: true };
        }
      }
    }
    
    // If at least one section is started and complete, form is valid
    // If no sections are started, form is invalid
    if (hasAnyStartedSection && hasAnyCompleteSection) {
      return null;
    }
    
    return { inValid: true };
  }

  private getTruckTypes(formModel: any): any[] {
    const truckTypes = [];

    const truckSections = [
      { key: STRAIGHT_TRUCK, display: 'Straight Truck' },
      { key: TRACTOR_AND_SEMI_TRAILER, display: 'Tractor and Semi-Trailer' },
      { key: TRACTOR_TWO_TRAILERS, display: 'Tractor-Two Trailers' },
      { key: OTHERS, display: 'Others' }
    ];

    truckSections.forEach(section => {
      const sectionData = formModel[section.key];
      let truckType;

      if (section.key === OTHERS) {
        truckType = new OtherTruckType(sectionData);
      } else {
        truckType = new TruckType(sectionData);
        truckType.otherTruckType = sectionData.otherTruckType || false;
        truckType.otherTruckTypeDescription = sectionData.otherTruckTypeDescription || '';
      }

      if (!truckType.monthsOfExperience) {
        const years = Number(sectionData.years) || 0;
        const months = Number(sectionData.months) || 0;
        truckType.monthsOfExperience = (years * 12 + months).toString();
      }

      truckType.type = section.key;
      truckType.displayType = section.display;

      if (Number(truckType.monthsOfExperience) > 0) {
        truckTypes.push(truckType);
      }
    });

    return truckTypes;
  }

  private disableForm(): void {
    this.form.get('straightTruck').disable();
    this.form.get('tractorAndSemiTrailer').disable();
    this.form.get('tractorTwoTrailers').disable();
    this.form.get('others').disable();
  }
}
